import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface GlobalAnalysisStyle {
  container_bg_color: string;
  container_border_color: string;
  container_border_width: number;
  container_border_radius: number;
  container_padding: number;
  text_color: string;
  heading_color: string;
  font_family: string;
  font_size: number;
  heading_size: number;
  line_height: number;
  line_color: string;
  line_opacity: number;
  line_width: number;
}

const GlobalStylesManager = () => {
  const styleElementRef = useRef<HTMLStyleElement | null>(null);

  // Memoized function to generate CSS
  const generateStylesCSS = useCallback((styles: GlobalAnalysisStyle) => `
    .analysis-container {
      background-color: ${styles.container_bg_color} !important;
      border: ${styles.container_border_width}px solid ${styles.container_border_color} !important;
      border-radius: ${styles.container_border_radius}px !important;
      padding: ${styles.container_padding}px !important;
      font-family: ${styles.font_family} !important;
      margin: 0 !important;
    }
    
    .analysis-container .prose {
      color: ${styles.text_color} !important;
      font-size: ${styles.font_size}px !important;
      line-height: ${styles.line_height} !important;
      max-width: none !important;
    }
    
    .analysis-container h1,
    .analysis-container h2,
    .analysis-container h3,
    .analysis-container h4,
    .analysis-container h5,
    .analysis-container h6 {
      color: ${styles.heading_color} !important;
      font-size: ${styles.heading_size}px !important;
      margin-top: 1em !important;
      margin-bottom: 0.5em !important;
    }

    .analysis-container hr {
      border: none !important;
      border-top: ${styles.line_width}px solid ${styles.line_color} !important;
      opacity: ${styles.line_opacity} !important;
      margin: 1.5em 0 !important;
    }

    .analysis-container p {
      margin: 1em 0 !important;
    }

    .analysis-container ul,
    .analysis-container ol {
      margin: 1em 0 !important;
      padding-left: 1.5em !important;
    }

    .analysis-container li {
      margin: 0.5em 0 !important;
    }
  `, []);

  // Memoized function to apply styles
  const applyStyles = useCallback((styles: GlobalAnalysisStyle) => {
    if (!styleElementRef.current) {
      styleElementRef.current = document.createElement('style');
      styleElementRef.current.id = 'analysis-styles';
      document.head.appendChild(styleElementRef.current);
    }

    const css = generateStylesCSS(styles);
    styleElementRef.current.textContent = css;
    console.log('Applied styles successfully');
  }, [generateStylesCSS]);

  // Memoized function to fetch and apply styles
  const fetchAndApplyStyles = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_active_analysis_styles');
      if (error) throw error;
      if (data) {
        console.log('Fetched active styles successfully');
        applyStyles(data);
      }
    } catch (err) {
      console.error('Error fetching styles:', err);
    }
  }, [applyStyles]);

  useEffect(() => {
    // Initial styles fetch
    fetchAndApplyStyles();

    // Set up realtime subscription
    const channel = supabase
      .channel('styles_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'global_analysis_styles',
          filter: 'is_active=eq.true'
        },
        (payload) => {
          if (payload.new) {
            console.log('Received style update');
            applyStyles(payload.new);
          }
        }
      )
      .subscribe((status) => {
        if (status.error) {
          console.error('Error subscribing to style changes:', status.error);
        } else {
          console.log('Successfully subscribed to style changes');
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAndApplyStyles, applyStyles]);

  return null;
};

export default GlobalStylesManager;